import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Clock, Zap, User, Wrench, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";

const GOTEAMUP_URL = "https://goteamup.com/p/13190668-kore-wellbeing/";

export default function ClassDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/classes/${slug}`).then((r) => setC(r.data)).catch(() => setC(null)); }, [slug]);
  if (!c) return <div className="container-kore py-40 text-kore-ink/50">Loading…</div>;
  const bookingUrl = c.booking_url || GOTEAMUP_URL;

  return (
    <div>
      <Seo
        title={`${c.name} — Kore Wellbeing class`}
        description={c.tagline ? `${c.name}: ${c.tagline}. Small-group coached class at Kore Wellbeing, Bowness-on-Windermere.` : `${c.name} at Kore Wellbeing — coached small-group class in Bowness-on-Windermere.`}
        path={`/classes/${slug}`}
        image={c.image_url}
        type="article"
      />
      <section className="relative h-[60vh] overflow-hidden">
        <img src={c.image_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-kore-forest/70" />
        <div className="relative container-kore h-full flex flex-col justify-end pb-14">
          <Link to="/classes" className="text-xs uppercase tracking-[0.22em] text-kore-warm/80 hover:text-kore-light flex items-center gap-2 mb-4"><ArrowLeft size={14} /> All classes</Link>
          <h1 className="font-display text-white text-6xl md:text-7xl font-light tracking-tight">{c.name}</h1>
          <p className="mt-3 text-kore-warm/85 text-lg italic font-display">{c.tagline}</p>
        </div>
      </section>

      <section className="container-kore py-20 grid md:grid-cols-3 gap-14">
        <div className="md:col-span-2 space-y-8">
          <p className="text-kore-ink/85 text-lg leading-relaxed">{c.description}</p>
          <div className="grid grid-cols-2 gap-6 border-t border-kore-gold/25 pt-8">
            <div><div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><Clock size={14} /> Duration</div><div className="mt-2 font-display text-2xl text-kore-forest">45–60 min</div></div>
            <div><div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><Zap size={14} /> Intensity</div><div className="mt-2 font-display text-2xl text-kore-forest">{c.difficulty}</div></div>
            <div><div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><User size={14} /> Coach</div><div className="mt-2 font-display text-2xl text-kore-forest">{c.instructor}</div></div>
            <div><div className="flex items-center gap-2 text-kore-gold text-xs uppercase tracking-[0.2em]"><Wrench size={14} /> Equipment</div><div className="mt-2 font-display text-2xl text-kore-forest">{c.equipment}</div></div>
          </div>
          {c.schedule?.length > 0 && (
            <div>
              <div className="text-kore-gold text-xs uppercase tracking-[0.2em]">Timetable</div>
              <div className="mt-4 flex flex-wrap gap-3">
                {c.schedule.map((s) => <span key={s} className="rounded-full border border-kore-gold/40 px-4 py-2 text-sm text-kore-forest">{s}</span>)}
              </div>
            </div>
          )}
        </div>

        <aside className="bg-white rounded-3xl p-8 border border-kore-gold/15 shadow-soft h-fit sticky top-28">
          <div className="text-kore-gold text-xs uppercase tracking-[0.24em]">Book on GoTeamUp</div>
          <h3 className="mt-3 font-display text-3xl text-kore-forest leading-tight">Reserve your spot.</h3>
          <p className="mt-4 text-sm text-kore-ink/70 leading-relaxed">
            Class bookings, memberships and free trials are handled securely by GoTeamUp. You'll create an account the first time you book — after that, everything sits in one place.
          </p>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="class-book-goteamup"
            className="btn-primary w-full mt-6"
          >
            Book on GoTeamUp <ArrowUpRight size={14} />
          </a>
          <Link to="/book" className="block text-center mt-4 text-xs uppercase tracking-[0.22em] text-kore-forest border-b border-kore-gold pb-1 w-fit mx-auto" data-testid="class-view-timetable">
            View full timetable →
          </Link>
        </aside>
      </section>
    </div>
  );
}
